import styled from 'styled-components';
import { Accordion, Button } from '@veneer/core';
import { primitives } from '@veneer/primitives';
import {
  layout,
  minDesktopLarge,
  minDesktopRegular,
  minDesktopSmall,
  minDesktopXS,
  minMobile,
  minTablet,
  paddingDesktopLarge,
  paddingDesktopRegular,
} from '../../GlobalStylesVisid';

const {
  color: { white, gray0, gray1, gray3, gray7, gray12 },
} = primitives;
const { size1, size2, size3, size4, size5, size6, size8 } = layout;

export const StyledAccordion = styled(Accordion)`
  > div:last-child {
    margin-bottom: 0px;
  }
`;

export const Title = styled.p`
  color: ${gray12};
`;

export const FaqContent = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${size4}px;
  ${minTablet} {
    row-gap: ${size5}px;
  }
  ${minDesktopSmall} {
    row-gap: ${size6}px;
  }
`;

const responsivePadding = `
  padding: ${size1}px;
  ${minDesktopXS} {
    padding: 10px;
  }
  ${minDesktopRegular} {
    padding: ${size3}px;
  }
`;

export const Question = styled.h4`
  box-sizing: border-box;
  ${responsivePadding}
`;

export const Answer = styled.p`
  ${responsivePadding}
`;

export const StyledList = styled.ul`
  margin: 0px;
  white-space: pre-wrap;
  color: ${gray7};
  list-style-type: disc;
  margin-block-start: 1em;
  margin-block-end: 1em;
  padding-inline-start: 24px;
`;

export const Container = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding: 40px ${size4}px;
  row-gap: ${size6}px;
  background-color: ${gray1};

  ${minMobile} {
    row-gap: 40px;
  }
  ${minTablet} {
    padding: 48px ${size8}px;
  }
  ${minDesktopXS} {
    padding: 80px ${size8}px;
  }
  ${minDesktopSmall} {
    padding: 80px 48px;
  }
  ${minDesktopLarge} {
    padding: 80px 156px;
  }
  ${minDesktopRegular} {
    padding-left: ${paddingDesktopRegular};
    padding-right: ${paddingDesktopRegular};
  }
  ${minDesktopLarge} {
    padding-left: ${paddingDesktopLarge};
    padding-right: ${paddingDesktopLarge};
  }
`;

export const RedirectButton = styled(Button)`
  width: fit-content;
  padding: 14px ${size5}px;
  align-self: center;
`;

const cancellationTableWidth = `
  max-width: 100%;
  ${minDesktopXS} {
    max-width: 715px;
  }

  ${minDesktopSmall} {
    max-width: 982px;
  }

  ${minDesktopRegular} {
    max-width: 1196px;
  }
  ${minDesktopLarge} {
    max-width: 1336px;
  }
`;

const standardTableWidth = `
  max-width: 100%;
  ${minDesktopXS} {
    max-width: 347px;
  }
  ${minDesktopSmall} {

    max-width: 379px;
  }
  ${minDesktopRegular} {
    max-width: 464px;
  }
  ${minDesktopLarge} {
    max-width: 520px;
  }
`;

export const Table = styled.table`
  width: 100%;

  align-self: center;
  border-collapse: separate;
  border-spacing: 0;
  display: table;
  table-layout: fixed;
  border: 1px solid ${gray3};
  border-radius: ${size2}px;
  overflow: hidden;

  ${(props) =>
    props.theme.tableType === 'cancellation'
      ? cancellationTableWidth
      : standardTableWidth}
`;

const cellStyles = `
  border-right: 1px solid ${gray3};
  text-align: center;
  vertical-align: middle;
`;

const firstCellLeftAlign = `
  &:first-child {
    text-align: left;
  }
`;

const lastCellNoBorder = `
  &:last-child {
    border-right: none;
  }
`;

export const TableHeader = styled.tr`
  > th {
    ${cellStyles}
    border-bottom: 1px solid ${gray3};
    padding: ${size2}px ${size3}px;
    box-sizing: border-box;
    color: ${gray7};
    background-color: ${white};
    font-weight: 400;
    ${firstCellLeftAlign}
    ${lastCellNoBorder}
  }
`;

export const TableBody = styled.tbody`
  color: ${gray12};
  > tr:nth-child(even) {
    background-color: ${white};
  }
  > tr:nth-child(odd) {
    background-color: ${gray0};
  }
  tr > td {
    border-bottom: 1px solid ${gray3};
  }
  tr:last-child > td {
    border-bottom: none;
  }
`;

export const TableRow = styled.tr`
  > td {
    ${cellStyles}
    padding: ${size2}px;
    color: ${gray12};
    ${firstCellLeftAlign}
    ${lastCellNoBorder}
  }
`;

export const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${size4}px;
  > div:first-child {
    border-radius: ${size2}px ${size2}px 0 0;
  }
  > div:last-child > div {
    border-radius: 0 0 ${size2}px ${size2}px;
  }
`;

const mobileCellBase = `
  flex: 1;
  border-right: 1px solid ${gray3};
  vertical-align: middle;
  text-align: center;
`;

export const MobileLabelRow = styled.div`
  display: flex;
  border: 1px solid ${gray3};
  background-color: ${white};
  justify-content: space-evenly;
  align-items: center;

  > p {
    ${mobileCellBase}
    padding: ${size3}px ${size1}px;
    margin-left: -1px;
    margin-right: -1px;
  }
`;

export const MobileDataRow = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${size3}px;
  color: ${gray12};
  width: 100%;

  > div {
    display: flex;
    flex-direction: row;
    border: 1px solid ${gray3};
    justify-content: center;
    background-color: ${gray0};

    > p {
      ${mobileCellBase}
      padding: ${size3}px ${size2}px;
      margin: 0;

      &:last-child {
        border-right: none;
      }
    }
  }
`;
